import { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormGroup,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  AddContractorAction,
  UpdateContractorAction,
} from "../../../../../store/Ancillary/Contractor/Action";

const initialFValues = {
  name: "",
  address: "",
  contact_person: "",
  mobile_number: "",
  office_number: "",
  country_id: "",
  state_id: "",
  city_id: "",
  zip_code: "",
  industry: "",
  about_company: "",
  website: "",
  company_lead_id: "",
  business_unit_id: "",
  email_id: "",
  billing_address: "",
  billing_name: "",
  gst_applicable: false,
  gst: "",
  sgst: "",
  cgst: "",
  igst: "",
  sac_code: "",
  hsn: "",
  status: "Active",
  is_same: false,
  lut_ref_no: "",
  tds_percentage: "0",
  employee_id: "",
  processing_fee_percentage: "0",
};

const ContractorConfigPopup = ({ editedRecord, setOpenPopup }) => {
  const [formData, setFormData] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (editedRecord) {
      setFormData({
        ...initialFValues,
        ...editedRecord,
        hsn: editedRecord?.hsn_code || "",
      });
    } else {
      setFormData(initialFValues);
    }
  }, [editedRecord]);

  const validate = (fieldValues = formData) => {
    let temp = { ...errors };

    if ("name" in fieldValues) {
      temp.name = fieldValues.name?.trim() ? "" : "Contractor Name is required";
    }

    if ("address" in fieldValues) {
      temp.address = fieldValues.address?.trim() ? "" : "Address is required";
    }

    if ("contact_person" in fieldValues) {
      temp.contact_person = fieldValues.contact_person?.trim()
        ? ""
        : "Contact Person is required";
    }

    if ("mobile_number" in fieldValues) {
      temp.mobile_number =
        fieldValues?.mobile_number?.toString() === ""
          ? "Mobile Number is required"
          : !fieldValues?.mobile_number
                ?.toString()
                ?.match(/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/)
            ? "Invalid Mobile Number."
            : "";
    }

    if ("email_id" in fieldValues) {
      temp.email_id =
        fieldValues.email_id === ""
          ? "Email ID is Required"
          : /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
                fieldValues.email_id,
              )
            ? ""
            : "Invalid Email.";
    }

    if (fieldValues.gst_applicable && "hsn" in fieldValues) {
      temp.hsn =
        fieldValues.hsn === ""
          ? "HSN is required"
          : /^\d{4}(\d{2}){0,2}$/.test(fieldValues.hsn)
            ? ""
            : "Enter valid HSN code";
    } else {
      temp.hsn = "";
    }

    if ("billing_address" in fieldValues) {
      temp.billing_address =
        fieldValues.billing_address === "" ? "Billing Address is required" : "";
    }

    if ("billing_name" in fieldValues) {
      temp.billing_name =
        fieldValues.billing_name === "" ? "Billing Name is required" : "";
    }

    setErrors(temp);

    return Object.values(temp).every((x) => x === "");
  };

  const handleChange = (event) => {
    const isApplicable = event.target.value === "true";

    setFormData((prev) => ({
      ...prev,
      gst_applicable: isApplicable,
      ...(isApplicable
        ? {
            sgst: "9",
            cgst: "9",
            igst: "0",
            sac_code: "",
            lut_ref_no: "",
          }
        : {
            sgst: "",
            cgst: "",
            igst: "",
            sac_code: "",
            hsn: "",
          }),
    }));

    if (!isApplicable) {
      setErrors((prev) => ({
        ...prev,
        hsn: "",
      }));
    }
  };

  const handleBillInfo = (event) => {
    const checked = event.target.checked;

    const updatedData = {
      ...formData,
      billing_address: checked ? formData?.address : "",
      billing_name: checked ? formData?.name : "",
      is_same: checked,
    };

    setFormData(updatedData);

    if (Object.values(errors).some((res) => res !== "")) {
      validate(updatedData);
    }
  };

  const handleInputChange = (event, value) => {
    // CGST / SGST should always be same
    if (["cgst", "sgst"].includes(event.target.name)) {
      const value = event.target.value;

      const updatedData = {
        ...formData,
        cgst: value,
        sgst: value,
      };

      setFormData(updatedData);

      if (Object.values(errors).some((res) => res !== "")) {
        validate(updatedData);
      }

      return;
    }

    const updatedData = {
      ...formData,
      [event.target.name]: event.target.value,
    };

    setFormData(updatedData);

    if (Object.values(errors).some((res) => res !== "")) {
      validate(updatedData);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSaving(true);

    try {
      if (editedRecord) {
        dispatch(
          UpdateContractorAction(formData, (res, err) => {
            if (res) {
              setOpenPopup(false);
            }
          }),
        );
      } else {
        dispatch(
          AddContractorAction(formData, (res, err) => {
            if (res) {
              setOpenPopup(false);
            }
          }),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              required
              size="small"
              label="Contractor Name"
              name="name"
              value={formData?.name}
              onChange={handleInputChange}
              {...(errors.name && {
                error: true,
                helperText: errors.name,
              })}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              required
              size="small"
              label="Contact Person"
              name="contact_person"
              value={formData?.contact_person}
              onChange={handleInputChange}
              {...(errors.contact_person && {
                error: true,
                helperText: errors.contact_person,
              })}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              required
              size="small"
              type="number"
              label="Mobile"
              name="mobile_number"
              value={formData?.mobile_number}
              onChange={handleInputChange}
              {...(errors.mobile_number && {
                error: true,
                helperText: errors.mobile_number,
              })}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              size="small"
              label="Industry"
              name="industry"
              value={formData?.industry}
              onChange={handleInputChange}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              required
              size="small"
              label="Contact Email"
              name="email_id"
              value={formData?.email_id}
              onChange={handleInputChange}
              {...(errors.email_id && {
                error: true,
                helperText: errors.email_id,
              })}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              size="small"
              label="GST Number"
              name="gst"
              value={formData?.gst || ""}
              onChange={(e) => {
                handleInputChange({
                  target: {
                    name: e.target.name,
                    value: e.target.value.toUpperCase(),
                  },
                });
              }}
            />
          </FormControl>
        </Grid>

        {/* TDS */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              size="small"
              type="number"
              label="TDS"
              name="tds_percentage"
              value={formData?.tds_percentage || ""}
              onChange={(e) => {
                const value = e.target.value;

                if (
                  value === "" ||
                  (Number(value) >= 0 && Number(value) <= 100)
                ) {
                  handleInputChange(e);
                }
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              {...(errors.tds_percentage && {
                error: true,
                helperText: errors.tds_percentage,
              })}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              size="small"
              type="number"
              label="Processing Fee"
              name="processing_fee_percentage"
              value={formData?.processing_fee_percentage || ""}
              onChange={(e) => {
                const value = e.target.value;

                if (
                  value === "" ||
                  (Number(value) >= 0 && Number(value) <= 100)
                ) {
                  handleInputChange(e);
                }
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              {...(errors.processing_fee_percentage && {
                error: true,
                helperText: errors.processing_fee_percentage,
              })}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              required
              size="small"
              label="Address"
              name="address"
              multiline
              rows={3}
              value={formData?.address}
              onChange={handleInputChange}
              {...(errors.address && {
                error: true,
                helperText: errors.address,
              })}
            />
          </FormControl>
        </Grid>

        {/* Bill To */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h6" sx={{ mr: 2 }}>
              Bill To
            </Typography>

            <FormGroup>
              <FormControlLabel
                disabled={!(formData?.name && formData?.address)}
                control={
                  <Checkbox
                    name="is_same"
                    checked={formData?.is_same || false}
                    onChange={handleBillInfo}
                  />
                }
                label="Same as Above"
              />
            </FormGroup>
          </Box>
        </Grid>

        {/* Billing Name */}
        <Grid item xs={12} md={6}>
          <TextField
            required
            size="small"
            fullWidth
            label="Billing Name"
            name="billing_name"
            value={formData?.billing_name}
            onChange={handleInputChange}
            {...(errors.billing_name && {
              error: true,
              helperText: errors.billing_name,
            })}
          />
        </Grid>

        {/* Billing Address */}
        <Grid item xs={12} md={6}>
          <TextField
            required
            size="small"
            multiline
            rows={3}
            fullWidth
            label="Billing Address"
            name="billing_address"
            value={formData?.billing_address}
            onChange={handleInputChange}
            {...(errors.billing_address && {
              error: true,
              helperText: errors.billing_address,
            })}
          />
        </Grid>

        {/* GST Applicable */}
        <Grid item xs={12}>
          <Typography variant="h6">GST applicable</Typography>

          <FormGroup row>
            <RadioGroup
              name="gst_applicable"
              row
              value={formData?.gst_applicable}
              onChange={handleChange}
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />

              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
          </FormGroup>
        </Grid>

        {/* GST Fields */}
        {formData?.gst_applicable ? (
          <>
            {/* HSN */}
            <Grid item xs={12} md={6}>
              <TextField
                required
                size="small"
                fullWidth
                label="HSN Number"
                name="hsn"
                placeholder="Enter HSN Code (4–8 digits)"
                value={formData?.hsn || ""}
                onChange={(e) =>
                  handleInputChange({
                    target: {
                      name: e.target.name,
                      value: e.target.value.toUpperCase(),
                    },
                  })
                }
                {...(errors.hsn && {
                  error: true,
                  helperText: errors.hsn,
                })}
              />
            </Grid>

            {/* SAC */}
            <Grid item xs={12} md={6}>
              <TextField
                size="small"
                fullWidth
                label="SAC Code"
                name="sac_code"
                value={formData?.sac_code}
                onChange={handleInputChange}
              />
            </Grid>

            {/* CGST */}
            <Grid item xs={12} md={6}>
              <TextField
                size="small"
                fullWidth
                type="number"
                label="CGST"
                name="cgst"
                value={formData?.cgst}
                onChange={(e) => {
                  const value = e.target.value;

                  if (
                    value === "" ||
                    (Number(value) >= 0 && Number(value) <= 100)
                  ) {
                    handleInputChange(e);
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* SGST */}
            <Grid item xs={12} md={6}>
              <TextField
                size="small"
                fullWidth
                type="number"
                label="SGST"
                name="sgst"
                value={formData?.sgst}
                onChange={(e) => {
                  const value = e.target.value;

                  if (
                    value === "" ||
                    (Number(value) >= 0 && Number(value) <= 100)
                  ) {
                    handleInputChange(e);
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* IGST */}
            <Grid item xs={12} md={6}>
              <TextField
                size="small"
                fullWidth
                type="number"
                label="IGST"
                name="igst"
                value={formData?.igst}
                onChange={(e) => {
                  const value = e.target.value;

                  if (
                    value === "" ||
                    (Number(value) >= 0 && Number(value) <= 100)
                  ) {
                    handleInputChange(e);
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  borderLeft: "4px solid rgb(139,68,255)",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  <strong>Note:</strong> Enter either IGST or CGST & SGST. If
                  IGST is provided, it will take priority and override
                  CGST/SGST.
                </Typography>
              </Box>
            </Grid>
          </>
        ) : (
          /* LUT */
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              fullWidth
              label="LUT Ref Number"
              name="lut_ref_no"
              value={formData?.lut_ref_no}
              onChange={(e) =>
                handleInputChange({
                  target: {
                    name: e.target.name,
                    value: e.target.value.toUpperCase(),
                  },
                })
              }
            />
          </Grid>
        )}
      </Grid>

      {/* ACTION */}
      <Box mt={3} display="flex" justifyContent="flex-end">
        <Button
          type="submit"
          variant="contained"
          size="medium"
          className="mpl-primary-btn"
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={18} disableShrink /> : null
          }
        >
          {editedRecord ? "Update" : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default ContractorConfigPopup;
